/**
 * PWA Update Prompt Component
 * Shows a notification when a new version of the app is available
 */

import { AnimatePresence, m } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import styles from './PWAUpdatePrompt.module.less';
import { RefreshCw, X } from '@/components/ui/icons';
import { subscribeToServiceWorkerRegistration } from '@/lib/pwa/service-worker';

const DISMISSED_UPDATE_KEY = 'til:pwa-update-dismissed';

interface PWAUpdatePromptProps {
  onUpdate?: () => void;
}

function isDismissed() {
  try {
    // UIUX-24: was sessionStorage, so "Later" reset every tab and the prompt
    // nagged again on each new one. localStorage keeps the dismissal across
    // tabs; a genuinely NEW version clears it (see handleUpdateFound).
    return localStorage.getItem(DISMISSED_UPDATE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function PWAUpdatePrompt({ onUpdate }: PWAUpdatePromptProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const isUpdatingRef = useRef(isUpdating);
  const reloadedRef = useRef(false);

  useEffect(() => {
    isUpdatingRef.current = isUpdating;
  }, [isUpdating]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    const sw = navigator.serviceWorker;
    let registration: ServiceWorkerRegistration | null = null;

    const showUpdatePrompt = () => {
      if (!isDismissed()) setShowPrompt(true);
    };
    const handleControllerChange = () => {
      if (isUpdatingRef.current && !reloadedRef.current) {
        reloadedRef.current = true;
        onUpdate?.();
        window.location.reload();
      }
    };
    const handleUpdateFound = () => {
      // A new version was found - a previous "Later" applied to the old one,
      // so the prompt must be allowed to show again (UIUX-24).
      try {
        localStorage.removeItem(DISMISSED_UPDATE_KEY);
      } catch {
        /* storage blocked; the prompt just shows as before */
      }
      const newWorker = registration?.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && sw.controller) showUpdatePrompt();
      });
    };
    const unsubscribe = subscribeToServiceWorkerRegistration((nextRegistration) => {
      registration = nextRegistration;
      registrationRef.current = nextRegistration;
      if (registration.waiting) showUpdatePrompt();
      registration.addEventListener('updatefound', handleUpdateFound);
    });

    sw.addEventListener('controllerchange', handleControllerChange);
    return () => {
      unsubscribe();
      sw.removeEventListener('controllerchange', handleControllerChange);
      registration?.removeEventListener('updatefound', handleUpdateFound);
    };
  }, [onUpdate]);

  const handleUpdate = () => {
    const waiting = registrationRef.current?.waiting;
    if (!waiting) return;

    setIsUpdating(true);
    try {
      waiting.postMessage({ type: 'SKIP_WAITING' });
    } catch {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_UPDATE_KEY, 'true');
    } catch {
      // A blocked storage API should not prevent dismissing the prompt.
    }
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <m.div
          className={styles.pwaUpdatePrompt}
          // UIUX-24: the prompt used to appear silently; role="status" makes
          // screen readers announce it on insertion.
          role="status"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={styles.titleGroup}>
                <div className={styles.indicator} />
                <h3 className={styles.title}>Update Available</h3>
              </div>
              <button
                onClick={handleDismiss}
                className={styles.closeButton}
                aria-label="Dismiss update"
              >
                <X className={styles.closeIcon} />
              </button>
            </div>

            <p className={styles.message}>
              A new version of TIL Dance is available. Refresh to get the latest features and
              improvements.
            </p>

            <div className={styles.actions}>
              <m.button
                onClick={handleUpdate}
                disabled={isUpdating}
                className={styles.updateButton}
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.02 }}
              >
                <RefreshCw
                  className={`${styles.updateIcon} ${isUpdating ? styles.spinning : ''}`}
                />
                <span>{isUpdating ? 'Updating...' : 'Update Now'}</span>
              </m.button>

              <button onClick={handleDismiss} className={styles.laterButton}>
                Later
              </button>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
